import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import Footer from "@/components/layout/Footer";
import { ArrowLeft, MessageCircle, Heart, Send, Paperclip, FileText, X, Lock } from "lucide-react";
import { toast } from "sonner";

const canPost = (u) => !!u && (u.is_admin || u.is_founder || u.is_founding_member || ["member", "collective", "paying", "pro"].includes(u.membership_tier));

const Discussion = () => {
  const { discussionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [discussion, setDiscussion] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReply, setNewReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [postAsBrand, setPostAsBrand] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    axios.get(`${API}/discussions/${discussionId}`)
      .then((res) => setDiscussion(res.data))
      .catch(() => navigate("/community"))
      .finally(() => setLoading(false));

    axios.get(`${API}/discussions/${discussionId}/replies`)
      .then((res) => setReplies(res.data))
      .catch(() => {});
  }, [discussionId, navigate]);

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to reply");
      return;
    }

    if (!newReply.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/discussions/${discussionId}/replies`,
        {
          content: newReply,
          post_as_brand: isAdmin && postAsBrand,
          attachment_url: attachment?.url || null,
          attachment_type: attachment?.type || null,
          attachment_name: attachment?.name || null,
        }
      );
      setReplies([...replies, response.data]);
      setNewReply("");
      setAttachment(null);
      setPostAsBrand(false);
      toast.success("Reply posted!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to post reply";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be 10MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload/discussion-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-main py-12">
          <div className="max-w-3xl mx-auto">
            <div className="skeleton h-8 w-1/3 mb-6" />
            <div className="skeleton h-12 w-full mb-4" />
            <div className="skeleton h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!discussion) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-main py-20 text-center">
          <h1 className="text-3xl font-display font-bold mb-4">Discussion not found</h1>
          <Link to="/community" className="text-[#5170FF] hover:text-[#FF2D87]">
            ← Back to Community
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="discussion-page">
      <Navbar />
      <DashboardHeader />

      <div className="container-main py-12">
        <div className="max-w-3xl mx-auto">
          {/* Back Link */}
          <Link
            to="/community"
            className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#0A0A0A] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Community
          </Link>

          {/* Discussion */}
          <article className="card-brutal p-8 mb-8" data-testid="discussion-content">
            <div className="flex gap-4 mb-6">
              {discussion.author_picture ? (
                <img
                  src={discussion.author_picture}
                  alt={discussion.author_name}
                  className="w-14 h-14 rounded-full border-2 border-[#0A0A0A] object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#5170FF] border-2 border-[#0A0A0A] flex items-center justify-center text-white text-xl font-bold">
                  {discussion.author_name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-bold">{discussion.author_name}</p>
                <p className="text-sm text-[#4A4A4A]">{formatDate(discussion.created_at)}</p>
              </div>
            </div>

            <span className="tag text-xs mb-4 inline-block capitalize">
              {discussion.category}
            </span>

            <h1 className="text-3xl font-display font-bold mb-6">{discussion.title}</h1>
            <div className="prose max-w-none">
              {discussion.content.split("\n").map((paragraph, index) => (
                <p key={index} className="mb-4 text-[#0A0A0A]">
                  {paragraph}
                </p>
              ))}
            </div>

            {discussion.attachment_type === "image" && discussion.attachment_url && (
              <a href={discussion.attachment_url} target="_blank" rel="noopener noreferrer" className="block mt-6">
                <img src={discussion.attachment_url} alt="" className="max-w-full border-2 border-[#0A0A0A]" loading="lazy" />
              </a>
            )}
            {discussion.attachment_type === "pdf" && discussion.attachment_url && (
              <a href={discussion.attachment_url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#FFC2E8] border-2 border-[#0A0A0A] text-sm font-bold hover:bg-[#FF2D87] hover:text-white transition-colors">
                <FileText className="w-4 h-4" /> {discussion.attachment_name || "View PDF"}
              </a>
            )}

            <div className="flex items-center gap-6 mt-8 pt-6 border-t border-[#E5E5E5]">
              <span className="flex items-center gap-2 text-[#4A4A4A]">
                <MessageCircle className="w-5 h-5" />
                {replies.length} replies
              </span>
              <span className="flex items-center gap-2 text-[#4A4A4A]">
                <Heart className="w-5 h-5" />
                {discussion.likes_count} likes
              </span>
            </div>
          </article>

          {/* Replies */}
          <section>
            <h2 className="text-xl font-display font-bold mb-6">
              Replies ({replies.length})
            </h2>

            {replies.length > 0 && (
              <div className="space-y-0 mb-8">
                {replies.map((reply) => (
                  <div
                    key={reply.reply_id}
                    className="discussion-reply"
                    data-testid={`reply-${reply.reply_id}`}
                  >
                    <div className="flex gap-3 mb-3">
                      {reply.author_picture ? (
                        <img
                          src={reply.author_picture}
                          alt={reply.author_name}
                          className="w-10 h-10 rounded-full border border-[#E5E5E5] object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#FFC2E8] flex items-center justify-center text-[#FF2D87] font-bold">
                          {reply.author_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-sm">{reply.author_name}</p>
                        <p className="text-xs text-[#4A4A4A]">{formatDate(reply.created_at)}</p>
                      </div>
                    </div>
                    <p className="text-[#0A0A0A]">{reply.content}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply Form */}
            <div className="card-brutal p-6" data-testid="reply-form">
              <h3 className="font-bold mb-4">Add a reply</h3>
              {user ? (
                canPost(user) ? (
                  <form onSubmit={handleSubmitReply}>
                    {isAdmin && (
                      <div className="bg-[#FFC2E8] p-3 border-2 border-[#0A0A0A] flex items-center justify-between mb-4" data-testid="reply-post-as-toggle">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A0A0A]">Replying as</p>
                          <p className="text-sm font-bold text-[#0A0A0A]">{postAsBrand ? "The Creative Ensemble" : (user?.name || "You")}</p>
                        </div>
                        <button type="button" onClick={() => setPostAsBrand(!postAsBrand)}
                          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-2 rounded-full transition-colors ${postAsBrand ? "bg-[#0A0A0A] text-white" : "bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"}`}
                          data-testid="reply-toggle-post-as-brand">
                          {postAsBrand ? "Switch to me" : "Reply as Brand"}
                        </button>
                      </div>
                    )}
                    <textarea
                      value={newReply}
                      onChange={(e) => setNewReply(e.target.value)}
                      placeholder="Share your thoughts..."
                      className="form-textarea mb-3"
                      rows={4}
                      data-testid="reply-input"
                    />
                    {/* Attachment */}
                    {!attachment ? (
                      <label className="inline-flex items-center gap-2 px-3 py-2 border-2 border-dashed border-[#0A0A0A] cursor-pointer hover:bg-[#FFC2E8] transition-colors text-xs font-bold mb-4">
                        <Paperclip className="w-4 h-4" />
                        {uploading ? "Uploading…" : "Attach image or PDF (max 10MB)"}
                        <input type="file" accept="image/*,application/pdf" onChange={handleAttachmentUpload} className="hidden" disabled={uploading} data-testid="reply-attachment-input" />
                      </label>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3 bg-[#FFC2E8] border-2 border-[#0A0A0A] mb-4 max-w-md" data-testid="reply-attachment-preview">
                        {attachment.type === "image" ? (
                          <img src={attachment.url} alt="" className="w-12 h-12 object-cover border border-[#0A0A0A]" />
                        ) : (
                          <FileText className="w-6 h-6 text-[#0A0A0A]" />
                        )}
                        <span className="text-sm font-bold flex-1 truncate">{attachment.name}</span>
                        <button type="button" onClick={() => setAttachment(null)} className="text-[#0A0A0A] hover:text-[#FF2D87]" data-testid="reply-remove-attachment-btn">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting || uploading}
                      className="btn-primary flex items-center gap-2"
                      data-testid="submit-reply-btn"
                    >
                      {submitting ? "Posting..." : "Post Reply"}
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                ) : (
                  <div className="bg-[#FFC2E8] border-2 border-[#0A0A0A] px-5 py-4 flex items-center gap-3" data-testid="reply-locked-banner">
                    <Lock className="w-4 h-4 text-[#0A0A0A]" />
                    <p className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">Replies are for paying members</p>
                  </div>
                )
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#4A4A4A] mb-4">Sign in to join the conversation</p>
                  <Link to="/login" className="btn-primary inline-flex">
                    Sign In
                  </Link>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Discussion;
